// API-Football service for World Cup 2026 live data
// All requests are proxied through /api/football to keep the API key server-side

const PROXY_BASE = '/api/football';

// World Cup 2026 league ID (FIFA World Cup)
const WORLD_CUP_LEAGUE_ID = 1; // FIFA World Cup
const WORLD_CUP_SEASON = 2026;

export interface ApiTeam {
  id: number;
  name: string;
  logo: string;
  code?: string;
}

export interface ApiGoals {
  home: number | null;
  away: number | null;
}

export interface ApiFixtureStatus {
  long: string;
  short: string;
  elapsed: number | null;
}

export interface ApiFixture {
  id: number;
  referee: string | null;
  timezone: string;
  date: string;
  timestamp: number;
  venue: { id: number | null; name: string | null; city: string | null };
  status: ApiFixtureStatus;
}

export interface ApiLeague {
  id: number;
  name: string;
  country: string;
  logo: string;
  flag: string | null;
  season: number;
  round: string;
}

export interface LiveFixture {
  fixture: ApiFixture;
  league: ApiLeague;
  teams: { home: ApiTeam; away: ApiTeam };
  goals: ApiGoals;
  score: {
    halftime: ApiGoals;
    fulltime: ApiGoals;
    extratime: ApiGoals;
    penalty: ApiGoals;
  };
  events?: FixtureEvent[];
}

export interface FixtureEvent {
  time: { elapsed: number; extra: number | null };
  team: { id: number; name: string; logo: string };
  player: { id: number | null; name: string };
  assist: { id: number | null; name: string | null };
  type: string;
  detail: string;
  comments: string | null;
}

export interface FixtureStat {
  team: { id: number; name: string; logo: string };
  statistics: Array<{ type: string; value: string | number | null }>;
}

export interface ApiStanding {
  rank: number;
  team: { id: number; name: string; logo: string; code?: string };
  points: number;
  goalsDiff: number;
  group: string;
  all: { played: number; win: number; draw: number; lose: number; goals: { for: number; against: number } };
}

export interface ApiLineup {
  team: { id: number; name: string; logo: string };
  formation: string;
  startXI: Array<{ player: { id: number; name: string; number: number; pos: string; grid: string | null } }>;
  substitutes: Array<{ player: { id: number; name: string; number: number; pos: string; grid: string | null } }>;
}

// In-memory cache (client-side)
const cache: Record<string, { data: unknown; ts: number }> = {};
const CACHE_TTL = 30_000; // 30 seconds

async function apiFetch<T>(endpoint: string, params: Record<string, string | number> = {}): Promise<T | null> {
  const cacheKey = endpoint + JSON.stringify(params);
  const now = Date.now();

  if (cache[cacheKey] && now - cache[cacheKey].ts < CACHE_TTL) {
    return cache[cacheKey].data as T;
  }

  // Always use the current window origin on the client so the proxy request
  // stays same-origin (avoids CORS failures in preview / staging environments).
  const base =
    typeof window !== 'undefined'
      ? window.location.origin
      : (process.env.NEXT_PUBLIC_SITE_URL || 'http://localhost:3000');

  const url = new URL(PROXY_BASE, base);
  url.searchParams.set('endpoint', endpoint);
  Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, String(v)));

  try {
    const res = await fetch(url.toString(), { cache: 'no-store' });

    if (!res.ok) {
      console.error(`[API-Football] HTTP ${res.status} for ${endpoint}`);
      if (cache[cacheKey]) return cache[cacheKey].data as T;
      return null;
    }

    const json = await res.json();

    // Check for API-level errors (e.g. token errors, rate limits)
    if (json.errors && typeof json.errors === 'object' && Object.keys(json.errors).length > 0) {
      console.error(`[API-Football] API errors for ${endpoint}:`, json.errors);
      if (cache[cacheKey]) return cache[cacheKey].data as T;
      return null;
    }

    if (json.error) {
      console.error(`[API-Football] Error: ${json.error}`);
      if (cache[cacheKey]) return cache[cacheKey].data as T;
      return null;
    }

    const data = json.response as T;
    cache[cacheKey] = { data, ts: now };
    return data;
  } catch (err) {
    console.error('[API-Football] Fetch error:', err);
    if (cache[cacheKey]) return cache[cacheKey].data as T;
    return null;
  }
}

/** Fetch all currently live World Cup 2026 fixtures.
 *  Uses status filter with league+season — combining live=all with league/season
 *  is not reliably supported by the API. Status codes cover all in-play states.
 */
export async function getLiveWorldCupFixtures(): Promise<LiveFixture[]> {
  const data = await apiFetch<LiveFixture[]>('/fixtures', {
    league: WORLD_CUP_LEAGUE_ID,
    season: WORLD_CUP_SEASON,
    status: '1H-HT-2H-ET-BT-P-INT-SUSP',
  });
  if (!data) return [];
  return data;
}

/** Fetch all World Cup 2026 fixtures (league=1&season=2026) */
export async function getAllFixtures(): Promise<LiveFixture[]> {
  const data = await apiFetch<LiveFixture[]>('/fixtures', {
    league: WORLD_CUP_LEAGUE_ID,
    season: WORLD_CUP_SEASON,
  });
  return data ?? [];
}

/** Fetch upcoming World Cup fixtures */
export async function getUpcomingFixtures(next = 10): Promise<LiveFixture[]> {
  // Use date-range approach: from today to +14 days (shorter window = more relevant results)
  // Status NS = Not Started (scheduled). Removed PST (postponed) to avoid clutter.
  const today = new Date();
  const future = new Date(today);
  future.setDate(future.getDate() + 14);

  const pad = (n: number) => String(n).padStart(2, '0');
  const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  const data = await apiFetch<LiveFixture[]>('/fixtures', {
    league: WORLD_CUP_LEAGUE_ID,
    season: WORLD_CUP_SEASON,
    from: fmt(today),
    to: fmt(future),
    status: 'NS-TBD',
  });

  if (!data || data.length === 0) {
    // Fallback: fetch without status filter and pick upcoming ones manually
    const allData = await apiFetch<LiveFixture[]>('/fixtures', {
      league: WORLD_CUP_LEAGUE_ID,
      season: WORLD_CUP_SEASON,
      from: fmt(today),
      to: fmt(future),
    });

    if (!allData) return [];

    const nowTs = Date.now();
    const upcoming = allData
      .filter(f => new Date(f.fixture.date).getTime() > nowTs)
      .sort((a, b) => new Date(a.fixture.date).getTime() - new Date(b.fixture.date).getTime());
    return upcoming.slice(0, next);
  }

  // Sort by date ascending and return the first `next` fixtures
  const sorted = [...data].sort(
    (a, b) => new Date(a.fixture.date).getTime() - new Date(b.fixture.date).getTime()
  );
  return sorted.slice(0, next);
}

/** Fetch events for a specific fixture */
export async function getFixtureEvents(fixtureId: number): Promise<FixtureEvent[]> {
  const data = await apiFetch<FixtureEvent[]>('/fixtures/events', { fixture: fixtureId });
  return data ?? [];
}

/** Fetch statistics for a specific fixture */
export async function getFixtureStatistics(fixtureId: number): Promise<FixtureStat[]> {
  const data = await apiFetch<FixtureStat[]>('/fixtures/statistics', { fixture: fixtureId });
  return data ?? [];
}

/** Fetch lineups for a specific fixture */
export async function getFixtureLineups(fixtureId: number): Promise<ApiLineup[]> {
  const data = await apiFetch<ApiLineup[]>('/fixtures/lineups', { fixture: fixtureId });
  return data ?? [];
}

/** Fetch a single fixture by ID */
export async function getFixtureById(fixtureId: number): Promise<LiveFixture | null> {
  const data = await apiFetch<LiveFixture[]>('/fixtures', { id: fixtureId });
  return data?.[0] ?? null;
}

/** Fetch World Cup standings */
export async function getWorldCupStandings(): Promise<ApiStanding[][]> {
  const data = await apiFetch<Array<{ league: { standings: ApiStanding[][] } }>>('/standings', {
    league: WORLD_CUP_LEAGUE_ID,
    season: WORLD_CUP_SEASON,
  });
  return data?.[0]?.league?.standings ?? [];
}

/** Fetch top scorers */
export async function getTopScorers(limit = 10): Promise<Array<{
  player: { id: number; name: string; photo: string; nationality: string };
  statistics: Array<{ team: { id: number; name: string; logo: string }; goals: { total: number | null; assists: number | null } }>;
}>> {
  const data = await apiFetch<Array<{
    player: { id: number; name: string; photo: string; nationality: string };
    statistics: Array<{ team: { id: number; name: string; logo: string }; goals: { total: number | null; assists: number | null } }>;
  }>>('/players/topscorers', {
    league: WORLD_CUP_LEAGUE_ID,
    season: WORLD_CUP_SEASON,
  });
  return (data ?? []).slice(0, limit);
}

/** Map API status short code to display label */
export function getStatusLabel(status: ApiFixtureStatus): string {
  const { short, elapsed } = status;
  switch (short) {
    case '1H': return `${elapsed ?? 0}'`;
    case 'HT': return 'HT';
    case '2H': return `${elapsed ?? 0}'`;
    case 'ET': return `ET ${elapsed ?? 0}'`;
    case 'BT': return 'Break';
    case 'P': return 'PEN';
    case 'SUSP': return 'SUSP';
    case 'INT': return 'INT';
    case 'FT': return 'FT';
    case 'AET': return 'AET';
    case 'PEN': return 'PEN';
    case 'PST': return 'PST';
    case 'CANC': return 'CANC';
    case 'ABD': return 'ABD';
    case 'AWD': return 'AWD';
    case 'WO': return 'WO';
    case 'NS': return 'NS';
    default: return short;
  }
}

export function isInPlay(status: ApiFixtureStatus): boolean {
  return ['1H', '2H', 'ET', 'P', 'BT', 'INT'].includes(status.short);
}

export function isHalfTime(status: ApiFixtureStatus): boolean {
  return status.short === 'HT';
}

export const TEAM_FLAGS: Record<string, string> = {
  France: '🇫🇷', Germany: '🇩🇪', Brazil: '🇧🇷', Argentina: '🇦🇷',
  Spain: '🇪🇸', Portugal: '🇵🇹', England: '🏴󠁧󠁢󠁥󠁮󠁧󠁿', Netherlands: '🇳🇱',
  Italy: '🇮🇹', Belgium: '🇧🇪', Croatia: '🇭🇷', Morocco: '🇲🇦',
  Japan: '🇯🇵', Mexico: '🇲🇽', USA: '🇺🇸', Canada: '🇨🇦',
  Uruguay: '🇺🇾', Colombia: '🇨🇴', Ecuador: '🇪🇨', Senegal: '🇸🇳',
  Ghana: '🇬🇭', Cameroon: '🇨🇲', Australia: '🇦🇺', 'South Korea': '🇰🇷',
  Poland: '🇵🇱', Switzerland: '🇨🇭', Denmark: '🇩🇰', Serbia: '🇷🇸',
  'Saudi Arabia': '🇸🇦', Iran: '🇮🇷', Qatar: '🇶🇦', Tunisia: '🇹🇳',
  'South Africa': '🇿🇦', Nigeria: '🇳🇬', 'Ivory Coast': '🇨🇮',
};

export function getFlag(name: string): string {
  return TEAM_FLAGS[name] ?? '🏳️';
}
